import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@core/utils/cn';
import { Spinner } from './Spinner';

/**
 * Replaces the Material button family and the four `.btn-*` global classes.
 *
 * The variants reproduce EFFECTIVE rendering, not the base rules. styles.scss:1363-1385
 * neutralised `.btn-outline` and `.btn-filled-primary` down to
 * `min-height: 32px; box-shadow: none; border-radius: 4px`, overriding their own local
 * `height: 36px; border-radius: 6px`. So 32px/4px is what users actually see.
 *
 * `.btn-primary-gradient` is NOT in that override list, so its `--shadow-md` and its
 * `translateY(-2px)` hover lift do still apply — kept on `variant="gradient"` only.
 *
 * RIPPLES ARE DROPPED. This is a deliberate, signed-off downgrade: styles.scss already
 * set `box-shadow: none !important` on every button class, so the ripple was the last
 * remaining Material tell in a UI that had otherwise been restyled into a flat
 * enterprise grid. Replaced with `transition-colors` + an `active:` tint.
 */
const button = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-sm ' +
    'text-base font-medium transition-colors select-none ' +
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ' +
    'disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        /** mat-raised-button color="primary" / .btn-filled-primary */
        filled: 'bg-accent text-white hover:bg-accent-hover active:bg-accent-hover',
        /** .btn-primary-gradient — keeps its elevation and hover lift */
        gradient:
          'bg-[image:var(--vm-primary-gradient)] text-white font-semibold shadow-pop ' +
          'transition-all hover:-translate-y-0.5 hover:shadow-modal',
        /** .btn-outline — neutral border */
        outline:
          'border border-line-strong bg-surface text-fg hover:bg-surface-2 hover:border-gray-400 ' +
          'active:bg-surface-3',
        /** .btn-outline-primary — 2px accent border, accent text */
        outlinePrimary:
          'border-2 border-accent bg-transparent font-semibold text-accent ' +
          'hover:bg-accent/[0.08] active:bg-accent/[0.12]',
        /** mat-button / mat-stroked-button with no border */
        text: 'bg-transparent text-accent hover:bg-accent-soft active:bg-accent-soft',
        /** Destructive confirm actions */
        danger: 'bg-danger text-white hover:brightness-110 active:brightness-95',
      },
      size: {
        sm: 'min-h-7 px-3 text-sm',
        /** 32px — the effective height after the override block */
        md: 'min-h-8 px-4',
        lg: 'min-h-9 px-5',
      },
      full: {
        true: 'w-full',
      },
    },
    defaultVariants: { variant: 'filled', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  /** Render as the child element (e.g. a router `<Link>`). */
  asChild?: boolean;
  /** Shows an inline 18px spinner and disables the button. */
  loading?: boolean;
  /** Icon rendered before the label. */
  startIcon?: ReactNode;
  /** Icon rendered after the label. */
  endIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    full,
    asChild,
    loading,
    startIcon,
    endIcon,
    disabled,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  if (asChild) {
    return <Slot className={cn(button({ variant, size, full }), className)}>{children}</Slot>;
  }

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(button({ variant, size, full }), className)}
      {...rest}
    >
      {loading ? <Spinner size={18} /> : startIcon}
      {children}
      {!loading && endIcon}
    </button>
  );
});
